import { useAccount } from '@src/contexts';
import { useGetBusinessIndustries, useGetBusinessTypes } from '@src/hooks/queries/application';
import { BusinessInformationFormData } from '../components/businessInformationStep';
import { useMemo } from 'react';
import { v4 } from 'uuid';
import { getBusinessInformationSchema } from '../utils';
import Api from '@sendsprint/api-types';
import ClientApi from '@src/types/client';
import { checkNAValues } from '@src/utils/checkNAValues';

interface IUseBusinessInformationFormOptions {
  businessInformationDetails:
    | ClientApi.Onboarding.GetBusinessInformationDetails.Response['data']
    | undefined;
}

const useBusinessInformationForm = ({
  businessInformationDetails
}: IUseBusinessInformationFormOptions) => {
  const { userAddress, user, isLoggedIn } = useAccount();
  const countryValue = userAddress?.country;

  const { data: businessTypes = [], isLoading: businessTypesLoading } = useGetBusinessTypes({
    enabled: isLoggedIn
  });
  const { data: businessIndustries = [], isLoading: businessIndustriesLoading } =
    useGetBusinessIndustries({ enabled: isLoggedIn });

  const businessIndustriesWithStringIds = businessIndustries.map((industry) => ({
    ...industry,
    id: String(industry.id)
  }));

  const initialValues: BusinessInformationFormData = useMemo(() => {
    const businessInfoDetails = businessInformationDetails?.businessInfomation;

    const typeOfBusiness = businessTypes
      .find((type) => type.name === businessInfoDetails?.businessType)
      ?.id.toString();
    const businessIndustry = businessIndustries
      .find((industry) => industry.name === businessInfoDetails?.businessIndustry)
      ?.id.toString();
    const defaultAddress = [
      {
        id: v4(),
        address: '',
        city: '',
        state: '',
        zipCode: '',
        show: true
      }
    ];
    const mappedAddress = businessInfoDetails?.address?.map(
      (address: ClientApi.Onboarding.GetBusinessInformationDetails.AddressArray) => ({
        id: v4(),
        address: address.address || '',
        city: address.city || '',
        state: String(address.stateId),
        zipCode: address.zipCode || '',
        show: true
      })
    );

    return {
      typeOfBusiness: checkNAValues(typeOfBusiness || ''),
      businessDescription: checkNAValues(businessInfoDetails?.about || ''),
      businessEmailAddress: checkNAValues(businessInfoDetails?.emailAddress || ''),
      businessIndustry: checkNAValues(businessIndustry || ''),
      businessName: checkNAValues(businessInfoDetails?.name || ''),
      businessPhoneNumber: checkNAValues(businessInfoDetails?.phoneNumber || ''),
      logo: checkNAValues(businessInfoDetails?.logoUrl || null),
      tradeName: checkNAValues(businessInfoDetails?.tradeName || ''),
      companyWebsiteUrl: checkNAValues(businessInfoDetails?.websiteUrl || ''),
      companyRegistrationNumber: checkNAValues(
        businessInfoDetails?.companyRegistrationNumber || ''
      ),
      taxIdentificationNumber: checkNAValues(businessInfoDetails?.taxIdentificationNumber || ''),
      useRegisteredAddress: false,
      addressInfo: checkNAValues(mappedAddress?.length ? mappedAddress : defaultAddress)
    };
  }, [businessInformationDetails, businessTypes, businessIndustries]);

  const validationSchema = getBusinessInformationSchema({
    businessCountry: userAddress?.country as Api.Model.CountryInitials
  });

  return {
    validationSchema,
    initialValues,
    businessIndustriesWithStringIds,
    businessTypesLoading,
    businessIndustriesLoading,
    user,
    countryValue,
    userAddress,
    businessTypes
  };
};

export default useBusinessInformationForm;
